/**
 * useStudyTimer — accumulates study seconds on a page while the tab is visible
 * and posts them to POST /api/me/study-time in periodic chunks (and on unmount).
 * Kept intentionally simple: only counts real elapsed visible time (no idle
 * detection), throttled to a 60-second flush cadence and capped at 2h/beacon
 * to align with the backend `StudyTimeBody` validator.
 */
import { useEffect, useRef } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FLUSH_EVERY_MS = 60_000; // flush every minute
const MAX_BEACON_SECONDS = 7200; // must not exceed StudyTimeBody upper bound (2h)

const postStudyTime = (seconds) => {
  if (!seconds || seconds < 1) return;
  const s = Math.min(MAX_BEACON_SECONDS, Math.floor(seconds));
  // sendBeacon works during pagehide/unmount and doesn't block navigation.
  try {
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([JSON.stringify({ seconds: s })], { type: "application/json" });
      navigator.sendBeacon(`${API}/me/study-time`, blob);
      return;
    }
  } catch (_) { /* fall through */ }
  // Fallback — best-effort ignore failure so a network hiccup never breaks the page.
  axios.post(`${API}/me/study-time`, { seconds: s }).catch(() => {});
};

export default function useStudyTimer() {
  const lastTick = useRef(Date.now());
  const pending = useRef(0);
  const visible = useRef(typeof document === "undefined" || document.visibilityState === "visible");
  const flushTimer = useRef(null);

  useEffect(() => {
    // Every second, add elapsed time to `pending` while visible.
    const tick = () => {
      const now = Date.now();
      if (visible.current) {
        pending.current += (now - lastTick.current) / 1000;
      }
      lastTick.current = now;
    };
    const tickId = setInterval(tick, 1000);

    // Periodic flush.
    flushTimer.current = setInterval(() => {
      tick();
      if (pending.current >= 30) {
        postStudyTime(pending.current);
        pending.current = 0;
      }
    }, FLUSH_EVERY_MS);

    const onVis = () => {
      // On becoming hidden, record what we have; on becoming visible reset the clock.
      const now = Date.now();
      if (visible.current) pending.current += (now - lastTick.current) / 1000;
      lastTick.current = now;
      visible.current = document.visibilityState === "visible";
    };
    document.addEventListener("visibilitychange", onVis);

    const onPageHide = () => {
      tick();
      if (pending.current >= 5) {
        postStudyTime(pending.current);
        pending.current = 0;
      }
    };
    window.addEventListener("pagehide", onPageHide);

    return () => {
      clearInterval(tickId);
      clearInterval(flushTimer.current);
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("pagehide", onPageHide);
      // Final flush on unmount / route change.
      const now = Date.now();
      if (visible.current) pending.current += (now - lastTick.current) / 1000;
      if (pending.current >= 5) postStudyTime(pending.current);
    };
  }, []);
}
